import type { HeaderVersion } from '../../core/types';
import { isFlagEnabled } from '../../core/hooks/useContentAvailableCheck';

const storageKey = 'headerVersion';

// A configured header version (VITE__HEADER_VERSION) is a hard override: it
// forces the header for everyone and bypasses AB testing and session/local
// storage entirely. Used to roll a version out to 100% without the experiment.
const getForcedHeaderVersion = (): HeaderVersion | null => {
    const forced = APP_ENV.HEADER_VERSION;
    if (!forced || forced === '#VITE__HEADER_VERSION#') {
        return null;
    }
    return forced as HeaderVersion;
};

export const getHeaderVersion = () => {
    return getForcedHeaderVersion() ?? sessionStorage.getItem(storageKey);
};

export const setOldHeader = () => {
    setHeaderVersion('v0');
};

export const setHeaderVersion = (headerVersion: HeaderVersion) => {
    // While a version is forced via config, ignore AB / URL writes — reads
    // already bypass storage, so keep it clean and avoid confusing state.
    if (getForcedHeaderVersion()) {
        return;
    }
    if (isFlagEnabled('allowSwitchingHeader')) {
        sessionStorage.setItem(storageKey, headerVersion);
    } else {
        sessionStorage.setItem(storageKey, 'v0');
    }
};

export const isOldHeader = () => {
    // A forced version wins even when the switching flag is off.
    if (!getForcedHeaderVersion() && !isFlagEnabled('allowSwitchingHeader')) {
        return true;
    }
    const version = getHeaderVersion() ?? '';
    return !version || ['v0', 'old'].includes(version.toLowerCase());
};

export const isV1Header = () => {
    return getHeaderVersion()?.toLowerCase().includes('v1');
};

export const isV2Header = () => {
    return getHeaderVersion()?.toLowerCase().includes('v2');
};
